import apiClient, { unwrap } from './client';

// Generate unique idempotency key for preventing duplicate requests
const generateIdempotencyKey = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Billing API — dynamic plan catalog and pricing
 * Product Owner can change limits/pricing in DB — these APIs return current values.
 */
export const billingApi = {
  /**
   * Create a pending registration for paid plan signup.
   * Returns: { token, email, planCode, expiresAt, message }
   */
  createPendingRegistration: async (data) => {
    const res = await apiClient.post('/auth/register/pending', data, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /**
   * Prepare payment for a pending registration.
   * Returns: { orderId, razorpayOrderId, amount, currency, key }
   */
  prepareRegistrationPayment: async (token) => {
    const res = await apiClient.get(`/billing/registration/${token}/payment`, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /**
   * Complete registration after successful payment.
   * Returns: { user, tokens, tenant, profile }
   */
  completeRegistration: async (token, razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    const res = await apiClient.post('/billing/complete-registration', {
      token,
      razorpayOrderId,
      razorpayPaymentId,
      razorpaySignature
    }, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /** Complete a registration the promo code made free (no gateway payment). Same result as completeRegistration. */
  completeFreeRegistration: async (token) => {
    const res = await apiClient.post(`/billing/registration/${token}/complete-free`, {}, {
      headers: { 'X-Idempotency-Key': generateIdempotencyKey() }
    });
    return unwrap(res);
  },

  /**
   * Get all active plans with pricing for the current user's country.
   * Returns: { plans: [...], countryCode, currency }
   */
  getPlans: async () => {
    const res = await apiClient.get('/billing/plans');
    return unwrap(res);
  },

  /**
   * Get details for a specific plan by code (free, growth, pro).
   */
  getPlanByCode: async (planCode) => {
    const res = await apiClient.get(`/billing/plans/${planCode}`);
    return unwrap(res);
  },

  /**
   * Get only paid plans (growth, pro) for the upgrade page.
   */
  getPaidPlans: async () => {
    const res = await apiClient.get('/billing/plans/paid');
    return unwrap(res);
  },

  /**
   * Get plans for a specific country (public preview endpoint).
   * Useful for displaying pricing before user registers.
   * moduleTypes: string or array of module codes — limits are merged across
   * all of them (e.g. ['POULTRY', 'DAIRY']) so a multi-module signup sees
   * everything it gets, not just one module's slice.
   */
  getPlansForCountry: async (countryCode, moduleTypes = 'POULTRY') => {
    const modules = Array.isArray(moduleTypes) ? moduleTypes : [moduleTypes];
    const query = modules.map((m) => `module=${encodeURIComponent(m)}`).join('&');
    const res = await apiClient.get(`/billing/plans/country/${countryCode}?${query}`);
    return unwrap(res);
  },

  /**
   * Create a new subscription order for plan upgrade or renewal.
   */
  createOrder: async (planCode, durationMonths, promoCode, isRenewal = false, renewSubUserIds = []) => {
    const res = await apiClient.post('/billing/orders', {
      planCode,
      durationMonths,
      promoCode,
      isRenewal,
      renewSubUserIds
    }, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /** Server-side promo pricing (no order is created). All return { valid, errorMessage, message, baseAmount, regularAmount, discountAmount, finalAmount, currency, ... }. */
  previewRegistrationPromo: async (data) => unwrap(await apiClient.post('/billing/registration/promo-preview', data)),
  previewOrderPromo: async (data) => unwrap(await apiClient.post('/billing/orders/promo-preview', data)),
  previewSubUserPromo: async (promoCode) => unwrap(await apiClient.post('/billing/orders/paid-sub-user/promo-preview', { promoCode })),

  createPaidSubUserOrder: async (data) => {
    const res = await apiClient.post('/billing/orders/paid-sub-user', data, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /**
   * Verify payment and activate subscription.
   */
  verifyPayment: async (razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    const res = await apiClient.post('/billing/orders/verify', {
      razorpayOrderId,
      razorpayPaymentId,
      razorpaySignature
    }, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /**
   * Get all subscription orders for the current tenant.
   */
  getOrders: async (page = 0, size = 20) => {
    const res = await apiClient.get(`/billing/orders?page=${page}&size=${size}`);
    return unwrap(res);
  },

  /**
   * Get a specific order by ID.
   */
  getOrder: async (orderId) => {
    const res = await apiClient.get(`/billing/orders/${orderId}`);
    return unwrap(res);
  },

  /**
   * Request a refund for an order.
   */
  requestRefund: async (orderId, refundType, reason, requestedAmount) => {
    const res = await apiClient.post('/billing/refunds', {
      orderId,
      refundType,
      reason,
      requestedAmount
    }, {
      headers: {
        'X-Idempotency-Key': generateIdempotencyKey()
      }
    });
    return unwrap(res);
  },

  /**
   * Get all refund requests for the current tenant.
   */
  getRefundRequests: async (page = 0, size = 20) => {
    const res = await apiClient.get(`/billing/refunds?page=${page}&size=${size}`);
    return unwrap(res);
  },

  /**
   * Get a specific refund request by ID.
   */
  getRefundRequest: async (requestId) => {
    const res = await apiClient.get(`/billing/refunds/${requestId}`);
    return unwrap(res);
  },
};
